import React, { useState, useEffect, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  fetchLatestGeneratedContent,
  type GeneratedContent,
} from "@/api/twitter";
import { LoaderCircle } from "lucide-react";

/**
 * A component that displays a list of existing tweets for selection
 * to be used in scheduled posting jobs.
 */
export const TweetSelector = ({
  personaId,
  onSelectTweet,
  selectedTweetId,
  highlightTweetId,
}: {
  personaId: string;
  onSelectTweet: (tweetId: string) => void;
  selectedTweetId: string | null;
  highlightTweetId?: string | null;
}) => {
  const [allTweets, setAllTweets] = useState<GeneratedContent[]>([]);

  // Ref for the container element to enable auto-scrolling
  const containerRef = useRef<HTMLDivElement>(null);

  // Ref for the highlighted tweet element
  const highlightedTweetRef = useRef<HTMLDivElement>(null);

  // Query to fetch tweets with pagination - using useInfiniteQuery properly
  const {
    data: fetchedTweets,
    isLoading,
    isError,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["selectableTweets", personaId],
    queryFn: ({ pageParam }) =>
      fetchLatestGeneratedContent(personaId, true, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.cursor,
    enabled: !!personaId,
    staleTime: 30000, // 30 seconds
  });

  // Update allTweets when fetchedTweets changes
  useEffect(() => {
    if (fetchedTweets?.pages) {
      const tweets = fetchedTweets.pages.flatMap((page) => page.data);
      setAllTweets(tweets);
    }
  }, [fetchedTweets]);

  // Auto-select the highlightTweetId if provided
  useEffect(() => {
    if (highlightTweetId && !selectedTweetId) {
      onSelectTweet(highlightTweetId);
    }
  }, [highlightTweetId, selectedTweetId, onSelectTweet]);

  // Scroll to the highlighted tweet once it's in the DOM
  useEffect(() => {
    // Check if we should load more tweets to find the highlighted tweet
    const shouldLoadMore =
      highlightTweetId &&
      !allTweets.some((tweet) => tweet.doc_id === highlightTweetId) &&
      hasNextPage &&
      !isFetchingNextPage;

    if (shouldLoadMore) {
      // Load more tweets to try to find the highlighted tweet
      fetchNextPage();
    }

    // Scroll to the highlighted tweet if it's in the DOM
    if (
      highlightTweetId &&
      highlightedTweetRef.current &&
      containerRef.current
    ) {
      highlightedTweetRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [
    highlightTweetId,
    allTweets,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  // Watch when user scrolls to the bottom of the container
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight * 1.5 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  if (isLoading && allTweets.length === 0) {
    return (
      <div className="flex justify-center items-center h-32">
        <LoaderCircle className="h-6 w-6 animate-spin stroke-accent" />
        <span className="ml-2">Loading tweets...</span>
      </div>
    );
  }

  if (isError && allTweets.length === 0) {
    return (
      <div className="text-center text-red-500 py-4">
        Error loading tweets. Please try again.
      </div>
    );
  }

  if (allTweets.length === 0) {
    return (
      <div className="text-center py-4">
        No tweets found. Generate some tweets first.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="space-y-4 max-h-64 overflow-y-auto p-2 border rounded-md"
      onScroll={handleScroll}
    >
      <div className="text-sm font-medium pb-2 border-b border-border">
        Select a tweet to post:
      </div>

      {allTweets.map((tweet) => (
        <div
          key={tweet.doc_id}
          ref={tweet.doc_id === highlightTweetId ? highlightedTweetRef : null}
          className={`
            p-3 border rounded-md cursor-pointer hover:bg-accent/10 transition-colors
            ${selectedTweetId === tweet.doc_id ? "border-primary bg-accent/20" : "border-border"}
            ${tweet.doc_id === highlightTweetId ? "ring-2 ring-primary" : ""}
          `}
          onClick={() => onSelectTweet(tweet.doc_id)}
        >
          <div className="text-sm">{tweet.text}</div>
          <div className="flex justify-between items-center mt-1">
            <div className="text-xs text-muted-foreground">
              {new Date(tweet.date).toLocaleString()}
            </div>
            {tweet.posted && (
              <div className="text-xs text-yellow-500 px-2 py-1 bg-yellow-950/30 rounded-full">
                Already posted
              </div>
            )}
          </div>
        </div>
      ))}

      {isFetchingNextPage && (
        <div className="flex justify-center py-2">
          <LoaderCircle className="h-4 w-4 animate-spin stroke-accent" />
          <span className="ml-2 text-xs">Loading more...</span>
        </div>
      )}

      {!hasNextPage && allTweets.length > 0 && (
        <div className="text-center text-xs text-muted-foreground py-2">
          No more tweets to load
        </div>
      )}
    </div>
  );
};
